import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

const RECOMMENDATIONS_CACHE_PREFIX = 'esbt-product-recommendations-v1:';
const RECOMMENDATIONS_CACHE_TTL = 5 * 60 * 1000;
const RECOMMENDATIONS_CACHE_MAX_BYTES = 200000;

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        if (result.data?.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /** Build a locale- and page-specific key so cached markup is never mixed. */
  #getPersistentCacheKey(url) {
    const locale = document.documentElement.lang || 'default';
    return `${RECOMMENDATIONS_CACHE_PREFIX}${locale}:${location.pathname}:${url}`;
  }

  #readPersistentRecommendation(url) {
    if (typeof sessionStorage === 'undefined' || Shopify.designMode) return null;

    try {
      const cached = JSON.parse(sessionStorage.getItem(this.#getPersistentCacheKey(url)) || 'null');
      if (!cached || typeof cached.data !== 'string' || Date.now() - cached.timestamp > RECOMMENDATIONS_CACHE_TTL) {
        return null;
      }

      return cached.data;
    } catch {
      return null;
    }
  }

  #writePersistentRecommendation(url, data) {
    if (typeof sessionStorage === 'undefined' || Shopify.designMode || data.length > RECOMMENDATIONS_CACHE_MAX_BYTES) return;

    try {
      sessionStorage.setItem(
        this.#getPersistentCacheKey(url),
        JSON.stringify({ timestamp: Date.now(), data })
      );
    } catch {
      // Storage can be unavailable or full; the in-memory cache still works.
    }
  }

  /**
   * Fetches the recommendations and caches the result for future use.
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    const persistentResponse = this.#readPersistentRecommendation(url);
    if (persistentResponse) {
      this.#cachedRecommendations[url] = persistentResponse;
      return { success: true, data: persistentResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      this.#writePersistentRecommendation(url, text);
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
